import React from 'react';
import { Alert, Box, Skeleton } from '@mui/material';
import { useFeature } from '../../ui/FeaturesProvider';
import { fetchPulseEconomics } from '../../services/manualDashboardService';
import type { ManualDashboardPayload } from '../../types/manualDashboard';
import { DATE_PRESETS, DataFreshness, PageHeader, resolvePreset, type DateRange, type FreshnessStatus } from '../../ui-kit';
import DashboardToolbar from '../manual-dashboard/components/DashboardToolbar';
import KpiRow from '../manual-dashboard/components/KpiRow';

/**
 * Pulse — how the leads marketing bought are doing, and what that costs.
 *
 * The figures come from the `pulse_economics` definition, so they are the same
 * numbers, worded the same way, as the docs that define them:
 * docs/features/contact-pulse-economics/. This page only lays them out.
 *
 * Built from the kit and the dashboards' own toolbar on purpose — a page in
 * this section has to look like a dashboard, because to the reader it is one.
 *
 * The toolbar carries the date range only for now; the channel filters need
 * their own options endpoint and come next.
 */

const DEFAULT_PRESET = DATE_PRESETS.last12Months;
const PRESETS = [
  DATE_PRESETS.thisMonth,
  DATE_PRESETS.lastMonth,
  DATE_PRESETS.thisQuarter,
  DATE_PRESETS.lastQuarter,
  DATE_PRESETS.yearToDate,
  DATE_PRESETS.last12Months,
];

export default function PulsePage() {
  const feature = useFeature('contact_optimization');
  const [payload, setPayload] = React.useState<ManualDashboardPayload | null>(null);
  const [range, setRange] = React.useState<DateRange>(() => resolvePreset(DEFAULT_PRESET));
  const [filters, setFilters] = React.useState<Record<string, string[]>>({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = React.useState(0);

  const filtersKey = JSON.stringify(filters);

  React.useEffect(() => {
    if (!feature.enabled) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchPulseEconomics(
      { start: range.start, end: range.end, filters: JSON.parse(filtersKey), refresh: refreshNonce > 0 },
      controller.signal,
    )
      .then(setPayload)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature.enabled, range.start, range.end, filtersKey, refreshNonce]);

  if (!feature.enabled) {
    return (
      <Alert severity="info">
        Contact Pulse is not turned on for this workspace. Ask an admin to enable it under Settings &rarr; Features.
      </Alert>
    );
  }

  const headline = payload?.blocks.find((b) => b.type === 'kpi_row');
  const freshness: FreshnessStatus | undefined = !payload
    ? undefined
    : payload.meta.refreshing
      ? 'updating'
      : payload.meta.status === 'failed'
        ? 'failed'
        : payload.meta.stale ? 'stale' : 'fresh';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        title="Pulse"
        description="Every lead marketing qualified, followed through contact, application and delivery — and what each step costs."
        meta={
          payload ? (
            <DataFreshness
              status={freshness}
              updatedAt={payload.meta.built_at ?? payload.meta.as_of}
              onRefresh={() => setRefreshNonce((n) => n + 1)}
              refreshing={loading && refreshNonce > 0}
            />
          ) : undefined
        }
      />

      <DashboardToolbar
        dateRange={range}
        onDateRangeChange={setRange}
        datePresets={PRESETS}
        minDate={payload?.history_start}
        dateDisplay="month"
        dateLabel="Lead dates"
        filters={[]}
        applied={filters}
        onApply={setFilters}
        loading={loading}
        optionsLoading={loading}
        chatOpen={false}
      />

      {error && <Alert severity="warning">{error}</Alert>}

      {loading && !payload ? (
        <Skeleton variant="rounded" height={132} />
      ) : (
        <KpiRow metrics={headline?.type === 'kpi_row' ? headline.metrics : []} loading={loading} />
      )}
    </Box>
  );
}
